import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { OrganizationMembership, Organization } from '@/services/types'
import api from '@/services/api'

interface OrgContextType {
  /** Currently selected organization (null if not in org view) */
  currentOrg: Organization | null
  /** User's role in the current org */
  orgRole: string | null
  /** List of clinics in the current org */
  orgClinics: Array<{ id: string; name: string; slug: string; city: string; state: string; status: string }>
  /** Whether the user is currently in org view */
  isOrgView: boolean
  /** Select an organization (switches to org view) */
  selectOrg: (orgId: string) => Promise<void>
  /** Exit org view (return to clinic view) */
  exitOrgView: () => void
  /** Loading state */
  isLoading: boolean
}

const OrganizationContext = createContext<OrgContextType | undefined>(undefined)

export const useOrganization = () => {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}

interface OrganizationProviderProps {
  children: ReactNode
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null)
  const [orgRole, setOrgRole] = useState<string | null>(null)
  const [orgClinics, setOrgClinics] = useState<Array<{ id: string; name: string; slug: string; city: string; state: string; status: string }>>([])
  const [isOrgView, setIsOrgView] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // On mount, check if there's a stored org selection
  useEffect(() => {
    const storedOrgId = localStorage.getItem('selectedOrgId')
    const memberships = user?.organizationMemberships

    if (storedOrgId && memberships && memberships.length > 0) {
      const membership = memberships.find((m: OrganizationMembership) => m.organization_id === storedOrgId)
      if (membership) {
        selectOrg(storedOrgId)
      } else {
        // Stored org no longer valid, clear it
        localStorage.removeItem('selectedOrgId')
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const selectOrg = useCallback(async (orgId: string) => {
    try {
      setIsLoading(true)

      // Fetch org details
      const orgResponse = await api.get(`/organizations/${orgId}`)
      const orgData = orgResponse.data?.data

      if (!orgData?.organization) {
        throw new Error('Organization not found')
      }

      setCurrentOrg(orgData.organization)
      setOrgRole(orgData.user_role)

      // Fetch org clinics
      const clinicsResponse = await api.get(`/organizations/${orgId}/clinics`)
      const clinicsData = clinicsResponse.data?.data?.clinics || []
      setOrgClinics(clinicsData)

      setIsOrgView(true)
      localStorage.setItem('selectedOrgId', orgId)
    } catch (error) {
      console.error('Failed to select organization:', error)
      setCurrentOrg(null)
      setOrgRole(null)
      setOrgClinics([])
      setIsOrgView(false)
      localStorage.removeItem('selectedOrgId')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const exitOrgView = useCallback(() => {
    setCurrentOrg(null)
    setOrgRole(null)
    setOrgClinics([])
    setIsOrgView(false)
    localStorage.removeItem('selectedOrgId')
  }, [])

  const value: OrgContextType = {
    currentOrg,
    orgRole,
    orgClinics,
    isOrgView,
    selectOrg,
    exitOrgView,
    isLoading,
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

export default OrganizationContext
